import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Minus, ArrowRight, Star } from "lucide-react";
import { api } from "@/lib/api";

// Premium package comparison: scannable summary cards on top + a detailed
// feature matrix below. Featured tier is highlighted throughout. Mobile-first.
export const PackagesComparison = () => {
  const [pkgs, setPkgs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/packages")
      .then(({ data }) => setPkgs(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="grid gap-5 md:grid-cols-3" data-testid="packages-loading">
        {[0, 1, 2].map((i) => (
          <div key={i} className="ascendia-card h-72 animate-pulse bg-white/[0.03]" />
        ))}
      </div>
    );
  }
  if (!pkgs.length) return null;

  const features = Object.keys(pkgs[0].features || {});
  const ctaFor = (name) => `/contact?service=${encodeURIComponent(name)}`;

  return (
    <div data-testid="packages-comparison">
      {/* Summary cards */}
      <div className="grid gap-5 md:grid-cols-3 md:items-stretch">
        {pkgs.map((p) => {
          const includedCount = Object.values(p.features || {}).filter(Boolean).length;
          return (
            <div
              key={p.id}
              data-testid={`package-card-${p.id}`}
              className={`relative flex flex-col rounded-2xl border p-6 transition-colors duration-200 ${
                p.featured
                  ? "border-[color:var(--border-strong)] bg-gradient-to-b from-gold/[0.08] to-transparent md:-translate-y-1 shadow-[0_0_0_1px_rgba(200,161,74,0.15)]"
                  : "border-[color:var(--border-subtle)] bg-white/[0.02] hover:border-[color:var(--border-strong)]"
              }`}
            >
              {p.featured && (
                <span className="absolute -top-3 left-6 inline-flex items-center gap-1 rounded-full bg-gold px-3 py-0.5 text-[11px] font-semibold text-black" data-testid="package-popular-badge">
                  <Star className="h-3 w-3" /> Most popular
                </span>
              )}
              <h3 className="font-display text-xl font-semibold text-white">{p.name}</h3>
              {p.summary && <p className="mt-2 text-sm text-secondary leading-relaxed">{p.summary}</p>}

              <p className="mt-4 text-xs text-muted-c">{p.price_label}</p>
              <p className="mt-1 text-[11px] uppercase tracking-[0.16em] text-gold-soft">{includedCount} of {features.length} inclusions</p>

              {Array.isArray(p.best_for) && p.best_for.length > 0 && (
                <div className="mt-4">
                  <p className="text-[10px] uppercase tracking-[0.18em] text-muted-c mb-2">Best for</p>
                  <div className="flex flex-wrap gap-1.5">
                    {p.best_for.slice(0, 4).map((b) => (
                      <span key={b} className="rounded-full border border-[color:var(--border-subtle)] bg-black/30 px-2.5 py-1 text-[11px] text-secondary">{b}</span>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-auto pt-6">
                <Link
                  to={ctaFor(p.name)}
                  data-testid={`package-cta-${p.id}`}
                  className={`inline-flex w-full items-center justify-center gap-2 rounded-xl px-5 h-11 text-sm font-semibold transition-[background-color,transform] duration-150 active:scale-[0.98] ${
                    p.featured
                      ? "bg-gold text-black hover:bg-gold-soft"
                      : "border border-[color:var(--border-strong)] text-white hover:bg-white/5"
                  }`}
                >
                  Get {p.name.replace("Ascendia ", "")} <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          );
        })}
      </div>

      {/* Feature matrix */}
      <div className="mt-10">
        <p className="mb-3 text-[11px] uppercase tracking-[0.18em] text-gold-soft">Compare what's included</p>
        <div data-testid="packages-comparison-table" className="overflow-x-auto rounded-2xl border border-[color:var(--border-subtle)]">
          <table className="w-full min-w-[640px] text-sm">
            <thead>
              <tr className="border-b border-[color:var(--border-subtle)]">
                <th className="p-5 text-left font-normal text-muted-c w-1/3">What's included</th>
                {pkgs.map((p) => (
                  <th key={p.id} className={`p-5 text-left ${p.featured ? "bg-gold/[0.05]" : ""}`}>
                    <div className="font-display text-base font-semibold text-white">{p.name}</div>
                    {p.featured && <span className="mt-1 inline-block text-[10px] uppercase tracking-[0.16em] text-gold-soft">Most popular</span>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {features.map((f) => (
                <tr key={f} className="border-b border-[color:var(--border-subtle)]/60 transition-colors hover:bg-white/[0.02]">
                  <td className="p-4 text-secondary">{f}</td>
                  {pkgs.map((p) => (
                    <td key={p.id} className={`p-4 ${p.featured ? "bg-gold/[0.03]" : ""}`}>
                      {p.features[f]
                        ? <Check className="h-5 w-5 text-gold" data-testid={`feature-yes-${p.id}`} />
                        : <Minus className="h-5 w-5 text-white/25" data-testid={`feature-no-${p.id}`} />}
                    </td>
                  ))}
                </tr>
              ))}
              <tr>
                <td className="p-4" />
                {pkgs.map((p) => (
                  <td key={p.id} className={`p-4 ${p.featured ? "bg-gold/[0.03]" : ""}`}>
                    <Link to={ctaFor(p.name)} className="inline-flex items-center gap-1.5 rounded-lg bg-gold px-4 py-2 text-xs font-semibold text-black hover:bg-gold-soft transition-colors">
                      Get this <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PackagesComparison;
